import {AsyncPipe, TitleCasePipe} from "@angular/common";
import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FlexModule} from "@angular/flex-layout";
import {MatButton} from "@angular/material/button";
import {MatDivider} from "@angular/material/divider";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {TabManagementService} from "@core-services/tab-management.service";
import {PageEventInterface} from "@core/interfaces/page-event.interface";
import {RetailerViewService} from "@features/leads/retailers/retailer-view/retailer-view.service";
import {LeaseViewComponent} from "@features/leasing/lease-view/lease-view.component";
import {
  AgreementType,
  LeaseSummary,
  OccupancyType,
  SpaceSelection,
  SubLeaseRequest
} from "@features/leasing/leasing.model";
import {SelectableCategory} from "@features/settings/industries/industry.model";
import {AppSharedModule} from "@modules/app-shared.module";
import {PanelType} from "@shared-components/info-panel/info-panel.component";
import {UtilsService} from "@shared-services/utils.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {SnackbarService} from "@ui-kit/framework/util/service/snackbar.service";
import {
  BookingConfig,
  BookingType,
  GridSpacePricingTimePeriod,
  PatternType,
  perPricingPeriod,
  Space,
  SpacePricingRateCategory
} from "../../bookings.model";
import {LeaseBookingsService} from "../../lease-bookings.service";

@Component({
  selector: 'app-booking-review-contract',
  standalone: true,
  imports: [
    FlexModule,
    MatButton,
    MatDivider,
    MatIcon,
    TitleCasePipe,
    AppSharedModule,
    AsyncPipe,
    MatTooltip
  ],
  templateUrl: './booking-review-contract.component.html',
  styleUrl: './booking-review-contract.component.scss'
})
export class BookingReviewContractComponent implements OnInit {
  @Input() offeringId!: string;
  @Input() offering!: any;
  @Input() isSubLease: boolean = false;

  @Input() bookingConfig: BookingConfig = new BookingConfig();
  @Input() spaces: Space[] = []

  @Input() editType: 'new' | 'add' | 'edit' = 'new';
  @Input() lease: LeaseSummary | null = null;
  @Input() parentLease: LeaseSummary | null = null;

  @Output() previous = new EventEmitter<void>();
  @Output() submit = new EventEmitter<boolean>();

  @Input() allIndustries: SelectableCategory[] = [];

  periodType: GridSpacePricingTimePeriod | null = null;
  $loading = this.loader.loading$;

  submitted = false;

  constructor(public utils: UtilsService,
              private bookingsService: LeaseBookingsService,
              private _snackBar: SnackbarService,
              private tabsService: TabManagementService,
              private retailerService: RetailerViewService,
              private loader: LoadingService) {
  }

  ngOnInit() {
    this.setTimePeriod();

    if (this.bookingConfig.retailerId && !this.bookingConfig.retailerCompanyName) {
      this.loadRetailer();
    }
  }

  loadRetailer() {
    this.retailerService.getRetailerDetails(this.bookingConfig.retailerId!.toString()).subscribe({
      next: value => {
        this.bookingConfig.retailerCompanyName = value.companyName;
        this.bookingConfig.userEmail = value.ownerEmail;
        this.bookingConfig.userPhoneNumber = value.phoneNumber ?? '';
      },
      error: err => {
        console.log(err);
      }
    });
  }

  setTimePeriod() {
    const period = Object.values(GridSpacePricingTimePeriod)
      .find(key => perPricingPeriod[key].patternType.includes(this.bookingConfig.patternType));
    if (period) this.periodType = period;
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString()
  }

  get patternDescription(): string {
    const basePattern = this.bookingConfig.patternType.replaceAll("_", " ");
    if (this.bookingConfig.patternType == PatternType.WEEKENDS && this.bookingConfig.includeDays.includes("Friday")) {
      return basePattern + " (includes Fridays)";
    }
    if (this.bookingConfig.patternType == PatternType.WEEKLY_CUSTOM) {
      const dayAbb = this.bookingConfig.includeDays.map(d => d.charAt(0).toUpperCase()).join(", ");
      return basePattern + ` (${dayAbb.toUpperCase()})`;
    }
    return basePattern;
  }

  get leasePeriodDays(): number {
    const sortedDates = this.bookingConfig.selectedDates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    const start = new Date(sortedDates[0]);
    const end = new Date(sortedDates[sortedDates.length - 1]);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  }

  get selectedSpaces(): Space[] {
    return this.spaces.filter(f => this.bookingConfig.spaceIds.includes(f.id));
  }

  get spaceNames(): string {
    if (this.isSubLease) this.parentLease?.spaceNames || '';
    return this.selectedSpaces
      .map(m => m.spaceName)
      .join(', ');
  }

  get spaceSize(): number {
    if (this.isSubLease) return this.bookingConfig.subLeaseSize;
    return this.selectedSpaces
      .map(m => m.spaceSize)
      .reduce((acc, size) => acc + size, 0);
  }

  get spaceLocation(): string {
    const space = this.spaces.find(f => this.bookingConfig.spaceIds.includes(f.id));
    if (space) return space.spaceLocation;
    return '';
  }

  get spaceIndustries() {
    const spaceIndustryCodes = this.selectedSpaces.flatMap(fm => fm.districtSpacesIndustries);
    return this.allIndustries.filter(f => spaceIndustryCodes.includes(f.code))
      .filter(f => !!f)
      .map(m => m.labelNameOverride ?? m.name)
      .join(', ');
  }

  get bookingEstimation() {
    return this.bookingConfig.bookingEstimation;
  }

  prevStep() {
    this.previous.emit();
  }

  submitBooking() {
    const minStartDate = this.bookingConfig.selectedDates
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];
    const maxEndDate = this.bookingConfig.selectedDates
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

    if (this.isSubLease) {
      const subLeaseRequest: SubLeaseRequest = {
        spaceUuid: null,
        leaseUuid: this.parentLease!.uuid,
        subLeaseUuid: (this.editType != 'new' && this.lease) ? this.lease.uuid : null,
        retailerId: this.bookingConfig.retailerId,
        offeringUuid: this.offeringId,
        occupiedSpace: this.bookingConfig.subLeaseSize,
        leaseStartDate: minStartDate,
        leaseEndDate: maxEndDate,
      }

      if (this.editType == 'new' || this.editType == 'edit') {
        this.submitSubLeaseApplication(subLeaseRequest);
      } else {
        this.addSubLeaseBookingPeriod(subLeaseRequest);
      }
    } else {
      const spaceSelection: SpaceSelection = {
        leaseUuid: (this.editType != 'new' && this.lease) ? this.lease.uuid : null,
        retailerId: this.bookingConfig.retailerId,
        districtUuid: this.offeringId,
        occupancyType: this.bookingConfig.occupancyType,
        agreementType: AgreementType.LEASE,
        startDate: minStartDate,
        endDate: maxEndDate,
        selectedFloorSpaceNames: [],
        editSpaces: false,
        periodGroupRanking: null,
        periodGroupUuid: null
      }

      if (this.editType == 'new' || this.editType == 'edit') {
        this.submitLeaseApplication(spaceSelection);
      } else {
        this.addLeaseBookingPeriod(spaceSelection);
      }
    }

  }

  submitLeaseApplication(spaceSelection: SpaceSelection) {
    this.bookingsService.submitLeaseBooking(spaceSelection, this.bookingConfig)
      .subscribe({
        next: (value: LeaseSummary) => {
          this._snackBar.openSnackBar("Created lease booking successfully!");
          if (this.editType == 'new') this.openLeaseViewTab(value.uuid, value.occupancyType!, value.agreementType!);
          this.submitted = true;
          this.submit.emit(this.submitted);
        },
        error: err => {
          this.submitted = false;
          this._snackBar.openSnackBar("Could not create lease booking. Please try again later.")
          console.error('BOOKING SUBMIT FAILED', err);
          this.submit.emit(this.submitted);
        }
      })
  }

  submitSubLeaseApplication(request: SubLeaseRequest) {
    console.log('SUB LEASE SUBMIT', request);
    this.bookingsService.submitSubLeaseBooking(request, this.bookingConfig)
      .subscribe({
        next: (value: LeaseSummary) => {
          this._snackBar.openSnackBar("Created sub-lease booking successfully!");
          if (this.editType == 'new') this.openLeaseViewTab(value.uuid, value.occupancyType!, value.agreementType!);
          this.submitted = true;
          this.submit.emit(this.submitted);
        },
        error: err => {
          this.submitted = false;
          this._snackBar.openSnackBar("Could not create sub-lease booking. Please try again later.")
          console.error('BOOKING SUBMIT FAILED', err);
          this.submit.emit(this.submitted);
        }
      })
  }

  addLeaseBookingPeriod(spaceSelection: SpaceSelection) {
    if (this.lease) {
      this.bookingsService.addLeaseBookingPeriod(this.lease!.uuid, spaceSelection, this.bookingConfig)
        .subscribe({
          next: (value: LeaseSummary) => {
            this._snackBar.openSnackBar("Added lease booking period successfully!");
            this.submitted = true;
            this.submit.emit(this.submitted);
          },
          error: err => {
            this.submitted = false;
            this._snackBar.openSnackBar("Could not add lease booking period. Please try again later.")
            console.error('BOOKING SUBMIT FAILED', err);
            this.submit.emit(this.submitted);
          }
        })
    }
  }

  addSubLeaseBookingPeriod(request: SubLeaseRequest) {
    if (this.lease) {
      this.bookingsService.addSubLeaseBookingPeriod(this.lease!.uuid, request, this.bookingConfig)
        .subscribe({
          next: (value: LeaseSummary) => {
            this._snackBar.openSnackBar("Added sub-lease booking period successfully!");
            this.submitted = true;
            this.submit.emit(this.submitted);
          },
          error: err => {
            this.submitted = false;
            this._snackBar.openSnackBar("Could not add sub-lease booking period. Please try again later.")
            console.error('BOOKING SUBMIT FAILED', err);
            this.submit.emit(this.submitted);
          }
        })
    }
  }

  openLeaseViewTab(id: string, occupancyType: OccupancyType, agreementType: AgreementType) {
    const payload: PageEventInterface = {
      componentToRegister: LeaseViewComponent,
      pageName: 'Lease ' + this.utils.displayUuid(id),
      pageHeader: this.offering.label,
      data: {id, occupancyType, agreementType},
      id: this.utils.generateUuidWithPrefix('lease'),
      offeringId: this.offering.offeringUuid,
      sectionCode: 'leasing',
      pageCode: 'lease-schedule',
      offering: this.offering
    }
    this.tabsService.sendPageAddEvent(payload);
  }

  protected readonly BookingType = BookingType;
  protected readonly PanelType = PanelType;
  protected readonly SpacePricingRateCategory = SpacePricingRateCategory;
  protected readonly OccupancyType = OccupancyType;
  protected readonly AgreementType = AgreementType;
}
